// src/components/tokenPage/performance/TokenPerformanceQualityCard.tsx

import React, { useEffect, useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { useToken } from "@/contexts";
import { useTokenPrice } from "@/contexts/token";
import {
  totalReturn,
  upsideCapture,
  downsideCapture,
  captureSpread,
  calculateWinRate,
  alphaHitRate,
} from "@/lib/metrics";

type RangeKey = '30d' | '90d' | '180d' | '365d';

/**
 * Capture & Efficiency Card
 * 
 * Shows quality metrics: upside/downside capture ratios, alpha, win rates.
 * All calculations use lib/metrics functions.
 */
const TokenPerformanceQualityCard: React.FC = () => {
  const { ticker } = useToken();
  const { aligned, isLoading } = useTokenPrice();
  
  // Destructure aligned returns for convenience
  const alignedReturns = aligned.tokenReturns;
  const alignedBtcReturns = aligned.btcReturns;
  const alignedEthReturns = aligned.ethReturns;
  
  const [range, setRange] = useState<RangeKey>('90d');
  const [bench, setBench] = useState<'BTC' | 'ETH'>('BTC');
  
  const tkr = ticker.toUpperCase();
  const benches = useMemo(() => (['BTC', 'ETH'] as const).filter(b => b !== tkr) as ('BTC' | 'ETH')[], [tkr]);

  // Ensure we never benchmark a token against itself
  useEffect(() => {
    if (bench === tkr) {
      setBench(benches[0] ?? (tkr === 'BTC' ? 'ETH' : 'BTC'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tkr]);

  const metrics = useMemo(() => {
    const defaultMetrics = {
      winRatePct: null as number | null,
      avgUpPct: null as number | null,
      avgDownPct: null as number | null,
      upCapX: null as number | null,
      downCapX: null as number | null,
      alphaPct: null as number | null,
      captureSpreadX: null as number | null,
      alphaHitRatePct: null as number | null,
      hasTokenData: false,
    };

    if (alignedReturns.length < 2) {
      return defaultMetrics;
    }

    // Get benchmark returns (already aligned from context)
    const allBenchReturns = bench === 'BTC' ? alignedBtcReturns : alignedEthReturns;
    
    // Slice by range (returns are already aligned, just slice to length)
    const targetLen = range === '30d' ? 30 : range === '90d' ? 90 : range === '180d' ? 180 : 365;
    const assetReturns = alignedReturns.slice(-targetLen);
    const benchReturns = allBenchReturns.slice(-targetLen);

    if (assetReturns.length === 0) {
      return defaultMetrics;
    }

    // Win rate and average up/down using lib/metrics
    const winRateMetrics = calculateWinRate(assetReturns);
    const wr = winRateMetrics.winRate;
    const avgU = winRateMetrics.avgUp !== null ? winRateMetrics.avgUp * 100 : null;
    const avgD = winRateMetrics.avgDown !== null ? winRateMetrics.avgDown * 100 : null;

    // Capture ratios using lib/metrics
    const upCap = upsideCapture(assetReturns, benchReturns);
    const downCap = downsideCapture(assetReturns, benchReturns);
    const spread = captureSpread(assetReturns, benchReturns);

    // Alpha (simple excess return) using lib/metrics
    const assetTotal = totalReturn(assetReturns);
    const benchTotal = totalReturn(benchReturns);
    const alpha = (assetTotal !== null && benchTotal !== null) 
      ? (assetTotal - benchTotal) * 100 
      : null;

    // Alpha hit rate using lib/metrics
    const alphaHitRatePct = alphaHitRate(assetReturns, benchReturns);

    return {
      winRatePct: wr,
      avgUpPct: avgU,
      avgDownPct: avgD,
      upCapX: upCap,
      downCapX: downCap,
      alphaPct: alpha !== null && Number.isFinite(alpha) ? alpha : null,
      captureSpreadX: spread,
      alphaHitRatePct,
      hasTokenData: true,
    };
  }, [alignedReturns, alignedBtcReturns, alignedEthReturns, range, bench]);

  const { winRatePct, avgUpPct, avgDownPct, upCapX, downCapX, alphaPct, captureSpreadX, alphaHitRatePct, hasTokenData } = metrics;

  // Hide if no token data available (after loading)
  if (!isLoading && !hasTokenData) {
    return null;
  }

  return (
    <DataCard
      title="Capture & Efficiency"
      subtitle={`Vs ${bench} • quality stats`}
      icon="activity"
      iconColor="text-cyan-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-800/50 rounded-lg p-1 border border-gray-700/40">
            {(benches.length ? benches : (['BTC', 'ETH'] as const)).map(k => (
              <button
                key={k}
                onClick={() => setBench(k)}
                className={`px-2.5 py-1 rounded-md text-[12px] font-medium ${
                  bench === k
                    ? 'bg-cyan-500/20 text-cyan-300'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                }`}
              >
                {k}
              </button>
            ))}
          </div>
          <TimeRangePicker
            options={['30d', '90d', '180d', '365d'] as const}
            value={range}
            onChange={setRange}
            theme="blue"
          />
        </div>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        <MetricBox label={`Upside Capture (${bench})`} value={upCapX} fmt="ratio2" suffix="x" toneOverride="positive" />
        <MetricBox label={`Downside Capture (${bench})`} value={downCapX} fmt="ratio2" suffix="x" toneOverride="negative" />
        <MetricBox label={`Capture Spread (Up−Down)`} value={captureSpreadX} fmt="ratio2" suffix="x" toneBySign />
        <MetricBox label={`Alpha vs ${bench}`} value={alphaPct} fmt="pct1" toneBySign />
        <MetricBox label={`Alpha Hit Rate (vs ${bench})`} value={alphaHitRatePct} fmt="pct1" toneOverride={alphaHitRatePct != null ? (alphaHitRatePct >= 50 ? 'positive' : 'negative') : undefined} />
        <MetricBox label="Win Rate (% of positive days)" value={winRatePct} fmt="pct1" toneOverride={winRatePct != null ? (winRatePct >= 50 ? 'positive' : 'negative') : undefined} />
        <MetricBox label="Avg return on positive days" value={avgUpPct} fmt="pct2" />
        <MetricBox label="Avg return on negative days" value={avgDownPct} fmt="pct2" />
      </div>
    </DataCard>
  );
};

export default TokenPerformanceQualityCard;
